import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Edit, Search, Bot, Database, Layers, Play, Copy, BarChart3 } from 'lucide-react'
import { aiGeneratorAPI } from '../../services/api'

const AIGeneratorPage = () => {
  const navigate = useNavigate()
  const [generators, setGenerators] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [cloning, setCloning] = useState(null)
  const [stats, setStats] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  useEffect(() => {
    fetchGenerators()
  }, [pagination.page, search])

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchGenerators = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search })
      }
      const response = await aiGeneratorAPI.getAll(params)
      if (response.data.status === 'success') {
        setGenerators(response.data.data.items)
        setPagination(prev => ({ ...prev, ...response.data.data.pagination }))
      }
    } catch (error) {
      console.error('Error fetching AI generators:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await aiGeneratorAPI.getHourlyStats(330) // IST +5:30
      if (response.data.status === 'success') {
        setStats(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this AI Generator?')) return
    try {
      await aiGeneratorAPI.delete(id)
      fetchGenerators()
      fetchStats()
    } catch (error) {
      console.error('Error deleting generator:', error)
      alert('Failed to delete generator')
    }
  }

  const handleClone = async (e, id) => {
    e.stopPropagation()
    try {
      setCloning(id)
      const response = await aiGeneratorAPI.clone(id)
      if (response.data.status === 'success') {
        fetchGenerators()
      }
    } catch (error) {
      console.error('Error cloning generator:', error)
      alert(error.response?.data?.detail || 'Failed to clone generator')
    } finally {
      setCloning(null)
    }
  }

  const handleEdit = (e, id) => {
    e.stopPropagation()
    navigate(`/dashboard/ai-generator/${id}`)
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-7 h-7 text-purple-600" />
            AI Generators
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage AI content generation configurations</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/ai-generator/create')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Generator
        </button>
      </div>

      {/* Hourly Stats */}
      <HourlyStatsChart stats={stats} />

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search generators..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : generators.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No AI Generators found</p>
            <button
              onClick={() => navigate('/dashboard/ai-generator/create')}
              className="mt-3 text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Create your first generator
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Knowledge</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fields</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Templates</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Executions</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {generators.map((gen) => (
                <tr
                  key={gen.id}
                  className="hover:bg-purple-50/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/dashboard/ai-generator/${gen.id}/executors`)}
                  title="Click to view executions"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{gen.name}</div>
                    {gen.description && (
                      <div className="text-xs text-gray-500 mt-0.5 max-w-md truncate">{gen.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <Database className="w-3 h-3" />
                      {gen.knowledgebaseCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <Layers className="w-3 h-3" />
                      {gen.fieldsCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {gen.templatesCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                      <Play className="w-3 h-3" />
                      {gen.executorsCount || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      gen.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {gen.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => handleEdit(e, gen.id)}
                        className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleClone(e, gen.id)}
                        disabled={cloning === gen.id}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                        title="Clone"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, gen.id)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={!pagination.hasPrev}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={!pagination.hasNext}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Hourly stats bar chart - designs generated today (IST) */
const HourlyStatsChart = ({ stats }) => {
  if (!stats) return null

  const maxCount = Math.max(1, ...stats.hourly.map(h => h.count))

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          <h2 className="text-sm font-semibold text-gray-900">Designs Generated Today</h2>
          <span className="text-xs text-gray-400">({stats.date} · {stats.timezone})</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">Total: <span className="font-semibold text-gray-900">{stats.total}</span></span>
          <span className="text-gray-500">Completed: <span className="font-semibold text-green-600">{stats.completed}</span></span>
        </div>
      </div>

      {/* Bar chart */}
      <div className="flex items-end gap-1 h-32">
        {stats.hourly.map((bucket) => (
          <div key={bucket.hour} className="flex-1 flex flex-col items-center justify-end group relative">
            {bucket.count > 0 && (
              <span className="text-[9px] text-gray-600 mb-0.5 font-medium">{bucket.count}</span>
            )}
            <div
              className={`w-full rounded-t transition-all ${bucket.count > 0 ? 'bg-purple-500 group-hover:bg-purple-600' : 'bg-gray-100'}`}
              style={{ height: `${bucket.count > 0 ? Math.max(6, (bucket.count / maxCount) * 100) : 3}%` }}
              title={`${bucket.label} - ${bucket.count} designs`}
            />
            <span className="text-[8px] text-gray-400 mt-1">{bucket.hour}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 text-center mt-2">Hour of day (IST)</p>
    </div>
  )
}

export default AIGeneratorPage
